import { ChevronDown, TimerIcon } from "lucide-react";
import React, { useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { format } from "date-fns";

const Repeat = ({ isOpen, onClose, onApply }) => {
    const [repeatType, setRepeatType] = useState("day");
    const [selectedTime, setSelectedTime] = useState("");
    const days = Array.from({ length: 31 }, (_, i) => i + 1);
    const [startDate, setStartDate] = useState(new Date()); // Default: today
    const [isStartDateOpen, setIsStartDateOpen] = useState(false);
    const [repeatNumber, setRepeatNumber] = useState(1);
    const [endOn, setEndOn] = useState("never");
    const [occurences, setOccurences] = useState(1);
    const [selectedWeekday, setSelectedWeekday] = useState("");
    const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const weekdayToIndex = {
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6,
    };
    const [monthRuleType, setMonthRuleType] = useState("day");
    const [monthDayValue, setMonthDayValue] = useState("1");
    const [monthOrdinal, setMonthOrdinal] = useState("first");
    const [monthWeekday, setMonthWeekday] = useState("monday");
    const [monthStartMonth, setMonthStartMonth] = useState("january");
    const [monthStarted, setMonthStarted] = useState(false);


    const getLastDayOfMonth = (year, monthIndex) => {
        return new Date(year, monthIndex + 1, 0).getDate();
    };

    const getNthWeekdayOfMonth = (year, monthIndex, weekday, nth) => {
        let date = new Date(year, monthIndex, 1);

        while (date.getDay() !== weekday) {
            date.setDate(date.getDate() + 1);
        }

        if (nth === "last") {
            let last = new Date(year, monthIndex + 1, 0);
            while (last.getDay() !== weekday) {
                last.setDate(last.getDate() - 1);
            }
            return last;
        }

        const offset = { first: 0, second: 7, third: 14, fourth: 21 }[nth];
        date.setDate(date.getDate() + offset);
        return date;
    };

    const weekdayMap = {
        sunday: 0, monday: 1, tuesday: 2,
        wednesday: 3, thursday: 4,
        friday: 5, saturday: 6,
    };

    const getMonthlyStartDate = () => {
        const year = startDate.getFullYear();
        const monthIndex = [
            "january", "february", "march", "april", "may", "june",
            "july", "august", "september", "october", "november", "december"
        ].indexOf(monthStartMonth);

        if (monthRuleType === "day") {
            let day = monthDayValue === "last"
                ? getLastDayOfMonth(year, monthIndex)
                : Number(monthDayValue);

            return new Date(year, monthIndex, day);
        }

        if (monthRuleType === "weekday") {
            return getNthWeekdayOfMonth(
                year,
                monthIndex,
                weekdayMap[monthWeekday],
                monthOrdinal
            );
        }
    };

    const getNextWeekdayDate = (start, targetWeekday) => {
        const startDay = start.getDay(); // 0-6
        const targetDay = weekdayToIndex[targetWeekday]; // 0-6

        let diff = targetDay - startDay;
        if (diff <= 0) diff += 7; // move to next week if same/previous day

        const nextDate = new Date(start);
        nextDate.setDate(start.getDate() + diff);
        return nextDate;
    };


    const openStartDateCalendar = () => {
        setIsStartDateOpen(true);
    };

    const applyStartDate = () => {
        setIsStartDateOpen(false);
    };

    const formatDisplayDate = (date) => {
        return format(date, "MMM d, yyyy");
    };

    if (!isOpen) return null;
    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl p-3 w-80 max-w-full">
                    <h2 className="text-lg font-semibold tracking-wide">Repeat task</h2>

                    <div className="flex items-center gap-2 mt-2">
                        {/* Number input */}
                        <div className="w-full max-w-16">
                            <input
                                type="number"
                                name="repeat-number"
                                min="1"
                                value={repeatNumber}
                                onChange={(e) => {
                                    const val = e.target.value;

                                    // Prevent negative or zero
                                    if (val === "" || Number(val) < 1) {
                                        setRepeatNumber(1);
                                    } else {
                                        setRepeatNumber(Number(val));
                                    }
                                }}
                                onBlur={() => {
                                    // If user clears field then clicks away → reset to 1
                                    if (!repeatNumber || repeatNumber < 1) {
                                        setRepeatNumber(1);
                                    }
                                }}
                                className="w-full py-1 px-3 rounded border border-gray-200 text-black outline-0 focus:border-blue-600 hover:border-blue-400"
                                placeholder="1"
                            />

                        </div>

                        {/* Select */}
                        <div className="relative w-full">
                            <select
                                name="repeat-type"
                                value={repeatType}
                                onChange={(e) => setRepeatType(e.target.value)}
                                className="w-full appearance-none py-1 px-3 rounded border border-gray-200 text-black 
                                   outline-0 focus:border-blue-600 hover:border-blue-400"
                            >
                                <option value="day">Day</option>
                                <option value="week">Week</option>
                                <option value="month">Month</option>
                                <option value="year">Year</option>
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                <ChevronDown size={16} />
                            </div>
                        </div>
                    </div>
                    {/* CONDITIONAL SECTIONS */}
                    <div className="mt-3">
                        {repeatType === "day" && (
                            <>
                                <div className="relative flex-1">
                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                        <ChevronDown size={16} />
                                    </div>
                                    <select
                                        value={selectedTime}
                                        onChange={(e) => setSelectedTime(e.target.value)}
                                        className="w-full appearance-none py-1 px-3 rounded border border-gray-200 text-black 
                                   outline-0 focus:border-blue-600 hover:border-blue-400"
                                    >
                                        <option value="">Set Time</option>
                                        {Array.from({ length: 24 * 4 }).map((_, i) => {
                                            const hours24 = Math.floor(i / 4);
                                            const minutes = (i % 4) * 15;
                                            const timeStr = `${String(hours24).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
                                            const displayHour = hours24 % 12 || 12;
                                            const period = hours24 < 12 ? "AM" : "PM";
                                            return (
                                                <option key={timeStr} value={timeStr}>
                                                    {displayHour}:{String(minutes).padStart(2, "0")} {period}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                                <div className="mt-3">
                                    <h4 className="text-sm font-semibold tracking-wide text-black mb-1">Starts</h4>
                                    <div
                                        onClick={openStartDateCalendar}
                                        className="w-full py-1 px-3 rounded bg-gray-100 cursor-pointer">
                                        {formatDisplayDate(startDate)}
                                    </div>
                                </div>
                            </>
                        )}

                        {repeatType === "week" && (
                            <>
                                <div className="flex gap-1 justify-between items-center">
                                    {weekdays.map((d) => (
                                        <button
                                            key={d}
                                            onClick={() => setSelectedWeekday(d)}
                                            className={`px-2 py-1 border rounded text-xs transition 
                ${selectedWeekday === d ? "bg-blue-600 text-white border-blue-600" : "hover:bg-gray-100"}`}
                                        >
                                            {d}
                                        </button>
                                    ))}
                                </div>

                                <div className="relative flex-1 mt-3">
                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                        <ChevronDown size={16} />
                                    </div>
                                    <select
                                        value={selectedTime}
                                        onChange={(e) => setSelectedTime(e.target.value)}
                                        className="w-full appearance-none py-1 px-3 rounded border border-gray-200 text-black 
                                   outline-0 focus:border-blue-600 hover:border-blue-400"
                                    >
                                        <option value="">Set Time</option>
                                        {Array.from({ length: 24 * 4 }).map((_, i) => {
                                            const hours24 = Math.floor(i / 4);
                                            const minutes = (i % 4) * 15;
                                            const timeStr = `${String(hours24).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
                                            const displayHour = hours24 % 12 || 12;
                                            const period = hours24 < 12 ? "AM" : "PM";
                                            return (
                                                <option key={timeStr} value={timeStr}>
                                                    {displayHour}:{String(minutes).padStart(2, "0")} {period}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                                <div className="mt-3">
                                    <h4 className="text-sm font-semibold tracking-wide text-black mb-1">Starts</h4>
                                    <div
                                        onClick={openStartDateCalendar}
                                        className="w-full py-1 px-3 rounded bg-gray-100 cursor-pointer">
                                        {formatDisplayDate(startDate)}
                                    </div>
                                    {selectedWeekday &&
                                        weekdayToIndex[selectedWeekday] !== startDate.getDay() && (
                                            <p className="mt-1 text-xs text-gray-700">
                                                First task starts on{" "}
                                                <span className="font-semibold text-blue-600">
                                                    {format(
                                                        getNextWeekdayDate(startDate, selectedWeekday),
                                                        "MMM d, yyyy"
                                                    )}
                                                </span>
                                            </p>
                                        )
                                    }

                                </div>
                            </>
                        )}

                        {repeatType === "month" && (
                            <>
                                <div className="flex flex-col gap-3">
                                    <div className="flex justify-start items-center gap-3">
                                        {/* Improved Blue Radio */}
                                        <input
                                            type="radio"
                                            name="repeat-month"
                                            checked={monthRuleType === "day"}
                                            onChange={() => setMonthRuleType("day")}
                                            className="
            h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-full
            border-2 border-gray-400
            checked:border-blue-600 checked:bg-blue-600
            transition-all
            checked:ring-2 checked:ring-blue-600 checked:ring-offset-2
        "
                                        />

                                        {/* Select with days */}
                                        <div className="relative w-full">
                                            <select
                                                value={monthDayValue}
                                                onChange={(e) => {
                                                    setMonthDayValue(e.target.value);
                                                    setMonthStarted(true);
                                                }}
                                                className="w-full appearance-none py-1 px-3 rounded border border-gray-200 text-black 
                       outline-0 focus:border-blue-600 hover:border-blue-400"
                                            >
                                                {days.map((d) => (
                                                    <option key={d} value={d}>Day {d}</option>
                                                ))}
                                                <option value="last">Last day</option>
                                            </select>

                                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                                <ChevronDown size={16} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-start items-center gap-3">
                                        {/* Improved Blue Radio */}
                                        <input
                                            type="radio"
                                            name="repeat-month"
                                            checked={monthRuleType === "weekday"}
                                            onChange={() => setMonthRuleType("weekday")}
                                            className="
            h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-full
            border-2 border-gray-400
            checked:border-blue-600 checked:bg-blue-600
            transition-all
            checked:ring-2 checked:ring-blue-600 checked:ring-offset-2
        "
                                        />

                                        <div className="relative w-full">
                                            <select
                                                value={monthOrdinal}
                                                onChange={(e) => {
                                                    setMonthOrdinal(e.target.value);
                                                    setMonthStarted(true);
                                                }}
                                                className="w-full appearance-none py-1 px-3 rounded border border-gray-200 text-black 
                       outline-0 focus:border-blue-600 hover:border-blue-400"
                                            >

                                                <option value="first">First</option>
                                                <option value="second">Second</option>
                                                <option value="third">Third</option>
                                                <option value="fourth">Fourth</option>
                                                <option value="last">Last</option>
                                            </select>

                                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                                <ChevronDown size={16} />
                                            </div>
                                        </div>
                                        <div className="relative w-full">
                                            <select
                                                value={monthWeekday}
                                                onChange={(e) => {
                                                    setMonthWeekday(e.target.value);
                                                    setMonthStarted(true);
                                                }}
                                                className="w-full appearance-none py-1 px-3 rounded border border-gray-200 text-black 
                       outline-0 focus:border-blue-600 hover:border-blue-400"
                                            >

                                                <option value="monday">Monday</option>
                                                <option value="tuesday">Tuesday</option>
                                                <option value="wednesday">Wednesday</option>
                                                <option value="thursday">Thursday</option>
                                                <option value="friday">Friday</option>
                                                <option value="saturday">Saturday</option>
                                                <option value="sunday">Sunday</option>

                                            </select>

                                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                                <ChevronDown size={16} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="relative flex-1">
                                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                            <ChevronDown size={16} />
                                        </div>
                                        <select
                                            value={selectedTime}
                                            onChange={(e) => setSelectedTime(e.target.value)}
                                            className="w-full appearance-none py-1 px-3 rounded border border-gray-200 text-black 
                                   outline-0 focus:border-blue-600 hover:border-blue-400"
                                        >
                                            <option value="">Set Time</option>
                                            {Array.from({ length: 24 * 4 }).map((_, i) => {
                                                const hours24 = Math.floor(i / 4);
                                                const minutes = (i % 4) * 15;
                                                const timeStr = `${String(hours24).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
                                                const displayHour = hours24 % 12 || 12;
                                                const period = hours24 < 12 ? "AM" : "PM";
                                                return (
                                                    <option key={timeStr} value={timeStr}>
                                                        {displayHour}:{String(minutes).padStart(2, "0")} {period}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <h4 className="text-sm font-semibold tracking-wide text-black mb-1">Starts</h4>
                                    <div className="relative w-full">
                                        <select
                                            value={monthStartMonth}
                                            onChange={(e) => {
                                                setMonthStartMonth(e.target.value);
                                                setMonthStarted(true);
                                            }}
                                            className="w-full appearance-none py-1 px-3 rounded text-black bg-gray-100 cursor-pointer"
                                        >

                                            <option value="january">January</option>
                                            <option value="february">February</option>
                                            <option value="march">March</option>
                                            <option value="april">April</option>
                                            <option value="may">May</option>
                                            <option value="june">June</option>
                                            <option value="july">July</option>
                                            <option value="august">August</option>
                                            <option value="september">September</option>
                                            <option value="october">October</option>
                                            <option value="november">November</option>
                                            <option value="december">December</option>

                                        </select>

                                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                            <ChevronDown size={16} />
                                        </div>
                                    </div>
                                    {monthStarted && (
                                        <p className="mt-2 text-xs text-gray-700">
                                            First task starts on:
                                            <span className="font-semibold text-blue-600 ml-1">
                                                {format(getMonthlyStartDate(), "MMM d, yyyy")}
                                            </span>
                                        </p>
                                    )}

                                </div>
                            </>
                        )}

                        {repeatType === "year" && (
                            <>
                                <div className="relative flex-1">
                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                        <ChevronDown size={16} />
                                    </div>
                                    <select
                                        value={selectedTime}
                                        onChange={(e) => setSelectedTime(e.target.value)}
                                        className="w-full appearance-none py-1 px-3 rounded border border-gray-200 text-black 
                                   outline-0 focus:border-blue-600 hover:border-blue-400"
                                    >
                                        <option value="">Set Time</option>
                                        {Array.from({ length: 24 * 4 }).map((_, i) => {
                                            const hours24 = Math.floor(i / 4);
                                            const minutes = (i % 4) * 15;
                                            const timeStr = `${String(hours24).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
                                            const displayHour = hours24 % 12 || 12;
                                            const period = hours24 < 12 ? "AM" : "PM";
                                            return (
                                                <option key={timeStr} value={timeStr}>
                                                    {displayHour}:{String(minutes).padStart(2, "0")} {period}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                                <div className="mt-3">
                                    <h4 className="text-sm font-semibold tracking-wide text-black mb-1">Starts</h4>
                                    <div
                                        onClick={openStartDateCalendar}
                                        className="w-full py-1 px-3 rounded bg-gray-100 cursor-pointer">
                                        {formatDisplayDate(startDate)}
                                    </div>
                                </div>
                            </>
                        )}

                    </div>

                    <div className="mt-3">
                        <h4 className="text-sm font-semibold tracking-wide text-black mb-2">Ends</h4>
                        <div className="flex flex-col gap-3">
                            <div className="flex justify-start items-center gap-3">
                                <input
                                    type="radio"
                                    name="end-on"
                                    value="never"
                                    checked={endOn === "never"}
                                    onChange={() => setEndOn("never")}
                                    className="
            h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-full
            border-2 border-gray-400
            checked:border-blue-600 checked:bg-blue-600
            transition-all
            checked:ring-2 checked:ring-blue-600 checked:ring-offset-2
        "
                                />
                                <p>Never</p>
                            </div>
                            <div className="flex justify-start items-center gap-3">
                                <input
                                    type="radio"
                                    name="end-on"
                                    value="on"
                                    checked={endOn === "on"}
                                    onChange={() => setEndOn("on")}
                                    className="
            h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-full
            border-2 border-gray-400
            checked:border-blue-600 checked:bg-blue-600
            transition-all
            checked:ring-2 checked:ring-blue-600 checked:ring-offset-2
        "
                                />
                                <div
                                    onClick={openStartDateCalendar}
                                    className="w-full py-1 px-3 rounded bg-gray-100 cursor-pointer">
                                    {formatDisplayDate(startDate)}
                                </div>
                            </div>
                            <div className="flex justify-start items-center gap-3">
                                <input
                                    type="radio"
                                    name="end-on"
                                    value="after"
                                    checked={endOn === "after"}
                                    onChange={() => setEndOn("after")}
                                    className="
            h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-full
            border-2 border-gray-400
            checked:border-blue-600 checked:bg-blue-600
            transition-all
            checked:ring-2 checked:ring-blue-600 checked:ring-offset-2
        "
                                />
                                <div
                                    className="w-full py-1 px-3 rounded bg-gray-100 cursor-pointer flex items-center gap-2">
                                    <span>
                                        <input
                                            type="number"
                                            name="repeat-number"
                                            min="1"
                                            value={occurences}
                                            onChange={(e) => {
                                                const val = e.target.value;

                                                // Prevent negative or zero
                                                if (val === "" || Number(val) < 1) {
                                                    setOccurences(1);
                                                } else {
                                                    setOccurences(Number(val));
                                                }
                                            }}
                                            onBlur={() => {
                                                // If user clears field then clicks away → reset to 1
                                                if (!occurences || occurences < 1) {
                                                    setOccurences(1);
                                                }
                                            }}
                                            placeholder="1"
                                            className="bg-transparent w-auto max-w-16 outline-0" />
                                    </span>
                                    Occurences
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 mt-5">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                onApply({ repeatType, every: repeatNumber, time: selectedTime, startsOn: startDate });
                                onClose();
                            }}
                            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                        >
                            Apply
                        </button>
                    </div>
                </div >
            </div>
            {/* Calendar Modal */}
            {isStartDateOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setIsStartDateOpen(false)}>
                    <div className="bg-white rounded-lg shadow-xl p-5 w-80 max-w-full" onClick={(e) => e.stopPropagation()}>
                        <Calendar
                            onChange={setStartDate}
                            value={startDate}
                            minDate={new Date()}
                        />

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3 mt-5">
                            <button
                                onClick={() => setIsStartDateOpen(false)}
                                className="px-4 py-2 text-sm rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={applyStartDate}
                                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Repeat;
